import { get } from 'lodash'
import { getAllUtmQueryParams, getTimeStamp } from './utils'

// Ported from universal-ovm-fe's `src/stores/auth/marketing-string.ts` (`MarketingString` class)
// and `src/stores/auth/selectors.ts` (`convertVisitorId`, `msExperiments`), trimmed to what
// old-ovm-fe needs: building the `utmPoa` payload the same auth-preserving way (existing
// first_visit/converted win over a fresh reset), reading the Convert visitor id, and
// merging/deduping the Convert `optimizely` array with what's already stored in the CRM marketing
// service's `marketingString` (now returned by old-ovm-be's own `/authenticate/{secret}` — see
// CrmService::getMarketingCase / ApiController::authenticateAction).

const parseMarketingString = (marketingString) => {
  if (typeof marketingString !== 'string' || marketingString.trim() === '') return {}
  try {
    const parsed = JSON.parse(marketingString)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

const getFromJson = (marketingString, path, fallback) => get(parseMarketingString(marketingString), path, fallback)

/** Mirrors universal-ovm-fe's `convertVisitorId` selector. */
export const getConvertVisitorId = (marketingString) => {
  const value = getFromJson(marketingString, 'convertVisitorId', '')
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (trimmed.length === 0 || trimmed.length > 256) return ''
  return trimmed
}

const parseJsonSafe = (value) => {
  try {
    return JSON.parse(value)
  } catch {
    return []
  }
}

const toIdString = (value) => {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

/** Coerce a raw record to the canonical string-id shape, or null when it lacks the ids. */
const normalizeExperiment = (raw) => {
  const record = raw || {}
  const experimentId = toIdString(record.experimentId)
  const variationId = toIdString(record.variationId)
  if (!experimentId || !variationId) return null
  return { campaignId: toIdString(record.campaignId), experimentId, variationId }
}

const identity = (record) => `${record.campaignId}|${record.experimentId}|${record.variationId}`

export const MarketingString = {
  UtmPoa: {
    /**
     * Build the `utmPoa` payload to post, preserving anything already stored (first_visit,
     * converted) rather than resetting it on every post — matches universal-ovm-fe's
     * `MarketingString.UtmPoa.Create`.
     */
    Create(marketingString) {
      const utmPoa = getFromJson(marketingString, 'utmPoa', {})
      return {
        utmPoa: {
          ...utmPoa,
          ...getAllUtmQueryParams(),
          first_visit: utmPoa.first_visit || getTimeStamp(),
          last_visit: getTimeStamp(),
          converted: utmPoa.converted || '',
        },
      }
    },
  },
  Experiments: {
    /**
     * Merge the Convert-reported active experiments with the experiments already stored in the
     * (auth-provided) marketing string and return the full `optimizely` array to persist.
     *
     * - Existing records are read RAW from the marketing string (no lossy filter).
     * - Ids are normalized to strings, so a stored numeric id and a Convert string id are treated
     *   as the same record rather than duplicated.
     * - The result never contains identical objects (deduped by campaignId+experimentId+variationId).
     * - Returns null when there are no active experiments or the merged set already matches what
     *   is stored, so we skip a pointless POST (the BE overwrites the key wholesale).
     */
    Sync(marketingString, active) {
      if (!active || !active.length) return null

      const stored = getFromJson(marketingString, 'optimizely', [])
      const existingRaw = typeof stored === 'string' ? parseJsonSafe(stored) : stored
      const existing = Array.isArray(existingRaw) ? existingRaw : []

      const merged = []
      const seen = new Set()
      existing.concat(active).forEach((raw) => {
        const record = normalizeExperiment(raw)
        if (!record) return
        const key = identity(record)
        if (seen.has(key)) return
        seen.add(key)
        merged.push(record)
      })

      const existingKeys = new Set(existing.map(normalizeExperiment).filter(Boolean).map(identity))
      const unchanged =
        merged.length === existingKeys.size && merged.every((record) => existingKeys.has(identity(record)))
      if (unchanged) return null

      return { optimizely: merged }
    },
  },
}
